import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, X, Ban, FileText } from 'lucide-react';
import type { LeaveRequestDto } from '@workforce/shared';
import { LEAVE_STATUS_LABELS } from '@workforce/shared';
import { api } from '@/lib/api';
import { fileUrl, fmtDays, fmtRange, statusTone } from '@/lib/leave';
import { useTz } from '@/lib/timezone';
import { t } from '@/lib/i18n';
import { Avatar, Badge, Button, ConfirmDialog, EmptyState, Field, Textarea } from './ui';

type Action = { kind: 'approve' | 'reject' | 'cancel'; req: LeaveRequestDto } | null;

export function LeaveRequestList({
  items,
  showEmployee = true,
  self = false,
  onChanged,
  empty,
}: {
  items: LeaveRequestDto[];
  showEmployee?: boolean;
  /** Use the self-service cancel endpoint. */
  self?: boolean;
  onChanged: () => void;
  empty?: string;
}) {
  const [action, setAction] = useState<Action>(null);
  const [comment, setComment] = useState('');
  const { fmt } = useTz();
  const run = useMutation({
    mutationFn: ({ kind, req, text }: { kind: 'approve' | 'reject' | 'cancel'; req: LeaveRequestDto; text: string }) => {
      if (kind === 'cancel') return api.post(self ? `/me/leave/requests/${req.id}/cancel` : `/leave/requests/${req.id}/cancel`, text ? { reason: text } : {});
      if (kind === 'reject') return api.post(`/leave/requests/${req.id}/reject`, { comment: text });
      return api.post(`/leave/requests/${req.id}/approve`, text ? { comment: text } : {});
    },
    onSuccess: () => {
      setAction(null);
      setComment('');
      onChanged();
    },
  });

  if (!items.length) return <EmptyState title={empty ?? t('No leave requests')} />;
  const open = (kind: 'approve' | 'reject' | 'cancel', req: LeaveRequestDto) => {
    run.reset();
    setComment('');
    setAction({ kind, req });
  };
  const needsCancelReason = action?.kind === 'cancel' && action.req.status === 'approved' && !self;

  return (
    <>
      <ul className="divide-y divide-slate-100">
        {items.map((r) => (
          <li key={r.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {showEmployee ? <Avatar name={r.employeeName} size="sm" /> : <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: r.color }} />}
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">
                  {showEmployee && <span>{r.employeeName} · </span>}
                  <span style={showEmployee ? { color: r.color } : undefined}>{t(r.leaveTypeName)}</span>
                  <span className="ms-2">
                    <Badge tone={statusTone(r.status)}>{t(LEAVE_STATUS_LABELS[r.status])}</Badge>
                  </span>
                </p>
                <p className="text-sm text-slate-600">
                  {fmtRange(r)} · <span className="font-medium">{fmtDays(r.days)}</span>
                </p>
                {r.reason && <p className="mt-0.5 text-xs text-slate-500">“{t(r.reason)}”</p>}
                {(r.document || r.autoAbsence) && (
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    {r.document && (
                      <a href={fileUrl(r.document.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline">
                        <FileText className="h-3.5 w-3.5" /> {t('Medical report')}
                      </a>
                    )}
                    {r.autoAbsence && <Badge tone="amber">{t('Absent day — taken from the balance')}</Badge>}
                  </p>
                )}
                <p className="mt-0.5 text-xs text-slate-400">
                  {r.requestedBy && showEmployee
                    ? t('Requested {date} by {name}', { date: fmt(r.createdAt, null, 'dd LLL yyyy'), name: r.requestedBy })
                    : t('Requested {date}', { date: fmt(r.createdAt, null, 'dd LLL yyyy') })}
                  {r.reviewedAt &&
                    ` · ${t(r.status === 'approved' ? 'Approved by {name}' : 'Rejected by {name}', { name: r.reviewedBy ?? t('system') })}${r.reviewComment ? `: “${r.reviewComment}”` : ''}`}
                  {r.cancelledAt && ` · ${t('Cancelled by {name}', { name: r.cancelledBy ?? t('system') })}${r.cancelReason ? `: “${r.cancelReason}”` : ''}`}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              {r.canReview && (
                <>
                  <Button size="sm" icon={<Check className="h-4 w-4" />} onClick={() => open('approve', r)}>
                    {t('Approve')}
                  </Button>
                  <Button size="sm" variant="secondary" icon={<X className="h-4 w-4" />} onClick={() => open('reject', r)}>
                    {t('Reject')}
                  </Button>
                </>
              )}
              {r.canCancel && (
                <Button size="sm" variant="ghost" icon={<Ban className="h-4 w-4" />} onClick={() => open('cancel', r)}>
                  {t('Cancel')}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={!!action}
        onClose={() => setAction(null)}
        title={
          action?.kind === 'approve' ? t('Approve leave?') : action?.kind === 'reject' ? t('Reject leave?') : t('Cancel leave?')
        }
        message={
          action && (
            <div className="space-y-1">
              <p>
                <strong>{action.req.employeeName}</strong> — {t(action.req.leaveTypeName)}
              </p>
              <p>
                {fmtRange(action.req)} ({fmtDays(action.req.days)})
              </p>
              {action.kind === 'approve' && <p className="text-xs text-slate-500">{t('The days are deducted from the balance and the employee is notified.')}</p>}
              {action.kind === 'cancel' && action.req.status === 'approved' && <p className="text-xs text-slate-500">{t('The days are returned to the balance.')}</p>}
            </div>
          )
        }
        confirmLabel={action?.kind === 'approve' ? t('Approve') : action?.kind === 'reject' ? t('Reject') : t('Cancel leave')}
        danger={action?.kind !== 'approve'}
        loading={run.isPending}
        error={run.error}
        confirmDisabled={(action?.kind === 'reject' || needsCancelReason) && comment.trim().length < 3}
        onConfirm={() => action && run.mutate({ kind: action.kind, req: action.req, text: comment.trim() })}
      >
        {action && (
          <Field
            label={action.kind === 'reject' ? t('Reason for rejection') : action.kind === 'approve' ? t('Comment (optional)') : needsCancelReason ? t('Reason') : t('Reason (optional)')}
            required={action.kind === 'reject' || needsCancelReason}
            hint={action.kind === 'reject' ? t('Shown to the employee.') : undefined}
          >
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000} />
          </Field>
        )}
      </ConfirmDialog>
    </>
  );
}
